import React, { useCallback, useEffect, useRef, useState } from 'react';

export default function EmailHtmlViewer({ srcDoc, title, onEscape }) {
  const frame = useRef(null);
  const escapeHandler = useRef(onEscape);
  escapeHandler.current = onEscape;
  const cleanup = useRef(() => {});
  const [height, setHeight] = useState(1);
  const measureDocument = useCallback(() => {
    cleanup.current();
    const element = frame.current;
    const document = element?.contentDocument;
    const body = document?.body;
    if (!body) return;
    // Only the parent may measure the document. Scripts remain disallowed by
    // both the sandbox and CSP; never combine this with allow-scripts.
    body.style.setProperty('height', 'auto', 'important');
    body.style.setProperty('min-height', '0', 'important');
    let active = true;
    let pendingFrame = null;
    const accordion = element.closest('details');
    const measure = () => {
      pendingFrame = null;
      if (!active || (accordion && !accordion.open) || !element.getBoundingClientRect().width) return;
      const style = document.defaultView.getComputedStyle(body);
      const margins = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      const next = Math.ceil(Math.max(body.scrollHeight, body.getBoundingClientRect().height) + margins);
      if (Number.isFinite(next)) setHeight(previous => previous === next ? previous : Math.max(1, next));
    };
    // Updating layout inside ResizeObserver delivery can trigger browser loop
    // errors. Batch measurements into the next animation frame instead.
    const schedule = () => {
      if (active && pendingFrame === null) pendingFrame = window.requestAnimationFrame(measure);
    };
    let previousWidth;
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(entries => {
      const needsMeasurement = entries.some(entry => {
        if (entry.target !== element) return true;
        const width = entry.contentRect.width;
        if (width === previousWidth) return false;
        previousWidth = width;
        return true;
      });
      if (needsMeasurement) schedule();
    });
    observer?.observe(body);
    // Width changes and reopening a collapsed accordion can reflow the email.
    observer?.observe(element);
    const onKeyDown = event => escapeHandler.current?.(event);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('load', schedule, true);
    window.addEventListener('resize', schedule);
    accordion?.addEventListener('toggle', schedule);
    measure();
    cleanup.current = () => {
      active = false;
      if (pendingFrame !== null) window.cancelAnimationFrame(pendingFrame);
      observer?.disconnect();
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('load', schedule, true);
      window.removeEventListener('resize', schedule);
      accordion?.removeEventListener('toggle', schedule);
    };
  }, []);
  useEffect(() => () => cleanup.current(), [srcDoc]);
  return <iframe ref={frame} className="mail-html" title={title} style={{ height }} onLoad={measureDocument}
    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" srcDoc={srcDoc} />;
}
